import { Box, Typography, Checkbox, FormControlLabel, Alert } from '@mui/material';
import {
  Controller,
  Control,
  UseFormWatch,
  UseFormSetValue,
  UseFormTrigger,
  UseFormGetValues,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { startOfDay } from 'date-fns';
import { AppDatePicker } from '@/shared/components';
import { SearchFilters } from '../types/search.types';

/** Returns YYYYMMDD integer from a Date using LOCAL calendar values — timezone-safe for day comparisons. */
const toLocalDay = (d: Date): number =>
  d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();

interface AvailabilityFilterProps {
  readonly control: Control<SearchFilters>;
  readonly watch: UseFormWatch<SearchFilters>;
  readonly setValue: UseFormSetValue<SearchFilters>;
  readonly trigger: UseFormTrigger<SearchFilters>;
  readonly getValues: UseFormGetValues<SearchFilters>;
  readonly dateError?: string | null;
  readonly onClearError?: () => void;
}

export function AvailabilityFilter({
  control,
  watch,
  setValue,
  trigger,
  getValues,
  dateError,
  onClearError,
}: Readonly<AvailabilityFilterProps>) {
  const { t } = useTranslation();
  const availabilityEnabled = watch('availabilityEnabled');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const wasEnabledRef = useRef(false);

  // Auto-populate startDate when availability filter is first enabled (only if startDate is null)
  useEffect(() => {
    if (availabilityEnabled && !wasEnabledRef.current && startDate === null) {
      setValue('startDate', startOfDay(new Date()));
    }
    wasEnabledRef.current = availabilityEnabled ?? false;
  }, [availabilityEnabled, startDate, setValue]);

  // Re-validate both date fields whenever either date value or the checkbox changes.
  // useEffect fires after render (once MUI picker state has settled), ensuring trigger
  // always runs against stable, fully-committed form values.
  useEffect(() => {
    void trigger(['startDate', 'endDate']);
  }, [startDate, endDate, availabilityEnabled, trigger]);

  return (
    <Box sx={{ mb: 3 }}>
      <Controller
        name="availabilityEnabled"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={field.value}
                sx={{
                  color: 'primary.main',
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('search.availability.label')}
              </Typography>
            }
          />
        )}
      />

      <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
        <Controller
          name="startDate"
          control={control}
          rules={{
            validate: (value: Date | null | undefined) => {
              if (!availabilityEnabled) return true;

              // "From" field is required when filter is enabled
              if (!value) {
                return t('search.availability.fromRequired');
              }

              // Prevent past dates
              if (toLocalDay(new Date(value)) < toLocalDay(new Date())) {
                return t('search.availability.fromPastError');
              }

              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <AppDatePicker
              label={t('search.availability.from')}
              value={field.value}
              onChange={value => {
                field.onChange(value);
                if (dateError && onClearError) {
                  onClearError();
                }
              }}
              onBlur={() => {
                field.onBlur();
                void trigger(['startDate', 'endDate']);
              }}
              minDate={new Date()}
              disabled={!availabilityEnabled}
              required={availabilityEnabled}
              error={!!error}
              helperText={error?.message as string}
            />
          )}
        />

        <Controller
          name="endDate"
          control={control}
          rules={{
            validate: (value: Date | null | undefined) => {
              if (!availabilityEnabled) return true;

              // "To" field is required when filter is enabled
              if (!value) {
                return t('search.availability.toRequiredWhenEnabled');
              }

              // "To" must be >= "From"
              // Use getValues to read the current committed value, avoiding stale closure
              const currentStartDate = getValues('startDate');
              if (currentStartDate) {
                const start = new Date(currentStartDate);
                const end = new Date(value);

                if (toLocalDay(end) < toLocalDay(start)) {
                  return t('search.availability.dateRangeError');
                }
              }

              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <AppDatePicker
              label={t('search.availability.to')}
              value={field.value}
              onChange={value => {
                field.onChange(value);
                if (dateError && onClearError) {
                  onClearError();
                }
              }}
              onBlur={() => {
                field.onBlur();
                void trigger(['startDate', 'endDate']);
              }}
              minDate={startDate || new Date()}
              disabled={!availabilityEnabled}
              required={availabilityEnabled}
              error={!!error}
              helperText={error?.message as string}
            />
          )}
        />

        {dateError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {dateError}
          </Alert>
        )}
      </Box>
    </Box>
  );
}
